// Подсказки первого раза: закрытый список.
//
// ## Зачем они и почему их мало
//
// Программой пользуются одни и те же три-пять человек каждый день. Обучающий слой поверх работы
// им не нужен: они выучат экран за неделю. Подсказка оправдана ровно там, где на экране есть
// правило, которое НЕ ВИДНО и о котором человек не догадается сам, — и цена незнания у этого
// правила настоящая.
//
// Отсюда потолок, тот же, что на телефонах: **одна подсказка за заход в раздел и один показ на
// подсказку за всё время**. Две затемнённые сцены подряд человек читает не как обучение, а как
// «программа не даёт работать».
//
// ## Почему список закрытый
//
// Новая подсказка — новая строка ЗДЕСЬ, а не свой компонент рядом. Так весь обучающий текст
// продукта виден одним куском и его можно перечитать целиком: сегодня его пять фраз, и он
// обязан оставаться таким, чтобы его читали.
//
// Порядок объявления — порядок показа: если на экране два непоказанных случая, покажется
// верхний.

export const УРОКИ = [
  // Сначала про работу: эти элементы живут в разделах и появляются, только когда человек туда
  // зашёл. Потом про инструменты полосы разделов — она на экране всегда, и стой она выше,
  // подсказки про работу не показались бы никогда.
  'порядок-подотчёта',
  // Три подсказки от 24.09.2026 — ответы на прямые вопросы заказчика: «где долги, которые
  // точно есть», «можем создавать ведомость по отдельности, а не всё кучей», «где посмотреть,
  // кому сколько авансов и удержаний за период». Всё это в программе было или появилось, а
  // найти это он не мог: правило есть, а на экране его не видно.
  'перенос-остатков',
  // Было «ведомость-по-объекту». Ключ заменён 01.10.2026 нарочно: подсказка сгорала впустую
  // (показ засчитывается при отрисовке, а кнопка, на которую она указывала, стояла на экране
  // только в месяце с ведомостями), и у тех, кто заходил в ведомости с 24.09, она могла уже
  // числиться показанной, хотя они её не увидели. Под старым ключом новый текст не показался
  // бы им никогда. Число уроков то же.
  'ведомости-галочками',
  'удержания-за-месяц',
  'способ-табеля',
  'ячейка-объясняет',
  'дела-это-очередь',
  'помощник',
  'тема-оформления',
] as const;

export type Урок = (typeof УРОКИ)[number];

export interface ТекстУрока {
  /** Одна короткая фраза, отвечающая на «что это». */
  заголовок: string;
  /** Что с этим делать. Не описание, а следующий шаг: без шага это просто ещё один текст. */
  текст: string;
  /**
   * Тот же шаг для экрана без клавиатуры — планшета, к которому её не подключили.
   *
   * Нужен там, где шаг назван клавишей. «Ctrl+K открывает поиск» на экранной клавиатуре — это
   * инструкция к несуществующему: клавиши Ctrl там нет вовсе. Подпись на самой кнопке поиска
   * прячет сочетание по тому же признаку, и подсказка не имеет права ей противоречить.
   */
  безКлавиатуры?: string;
}

export const ТЕКСТЫ: Readonly<Record<Урок, ТекстУрока>> = {
  'порядок-подотчёта': {
    заголовок: 'Сверху тот, у кого висит дольше',
    текст:
      'Список отсортирован по давности неподтверждённого остатка, а не по сумме: деньги, о которых забыли, дороже крупных, но свежих. Нужен другой порядок — переключите здесь.',
  },
  'перенос-остатков': {
    заголовок: 'Старые долги — одним листом',
    текст:
      'Долги, которые были до работы в программе, — форма, билеты, обучение — переносятся здесь сразу у всех: вставьте лист из Excel с фамилией, видом долга и суммой. Программа сначала покажет, кого нашла, и запишет только после вашего нажатия.',
  },
  'ведомости-галочками': {
    заголовок: 'Ведомость можно создать по одному объекту',
    текст:
      'Отметьте объекты галочками: на каждый будет своя ведомость. Все объекты сразу: ссылка внизу окна.',
  },
  'удержания-за-месяц': {
    заголовок: 'Удержания за месяц',
    текст:
      'На этой вкладке видно, кому сколько удержали за месяц: аванс, билеты, форма, обучение. Нажмите на вид удержания — откроется список людей с суммами.',
  },
  'способ-табеля': {
    заголовок: 'Табель ведётся одним из двух способов',
    текст:
      'Итогом за месяц — одно число часов на человека; по сменам — сетка по дням, из которой часы считаются сами. У заказчика итогом ведутся почти все объекты. Переключение гасит часы прежнего способа, и система спросит перед этим.',
  },
  'ячейка-объясняет': {
    заголовок: 'Ячейку можно спросить, откуда число',
    текст:
      'Считанные значения не вводятся руками, но раскрываются: нажмите — и увидите, из чего оно сложилось и где правка законна. Заблокированная ячейка называет причину.',
  },
  помощник: {
    заголовок: 'Спросить можно словами',
    текст:
      'Ctrl+K открывает поиск и вопросы: «сколько висит на Багровском», «кто не отчитывается». Числа помощник берёт из системы и показывает, откуда взял.',
    безКлавиатуры:
      'Здесь открываются поиск и вопросы: «сколько висит на Багровском», «кто не отчитывается». Числа помощник берёт из системы и показывает, откуда взял.',
  },
  'дела-это-очередь': {
    заголовок: 'Здесь только то, что ждёт решения',
    текст:
      'Дела с нулём не показываются: пустой раздел означает, что решать сейчас нечего. Каждая строка ведёт туда, где это решается, с уже наложенным отбором.',
  },
  'тема-оформления': {
    заголовок: 'Светлая, тёмная или как в системе',
    текст:
      'Оформление выбирается здесь и запоминается на этом устройстве. По умолчанию программа идёт за настройкой системы.',
  },
};
